import { MoreVertical } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { ArticleListItem } from '@shared/api';
import { isApiError } from '@shared/api';
import { useDebouncedUrlSearch } from '@shared/lib';
import {
  Button,
  Card,
  CardContent,
  CardHeaderRow,
  DescriptionList,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  FilterPopover,
  FilterPopoverFields,
  FilterPopoverSelect,
  Heading,
  InfoHint,
  PaginationControls,
  ResponsiveTable,
  SearchFilterToolbar,
  SearchInput,
  StatusBadge,
  Text,
  type ResponsiveTableColumn,
} from '@shared/ui';
import { articleListQuery } from '../api/queries';
import {
  articlePageSize,
  articleSearchSchema,
  toArticleQuery,
  type ArticleSearch,
} from '../model/search';
import { articleStatusLabel, articleTopicLabel } from '../model/presentation';
import { ArticleError } from './ArticleError';
import { ArticleListDeletion } from './ArticleListDeletion';

const knowledgeStatusTones = {
  ready: 'positive',
  pending: 'informational',
  empty: 'neutral',
  unavailable: 'warning',
  failed: 'negative',
  unknown: 'warning',
} as const;

export interface ArticleTopicsState {
  topics: readonly NonNullable<ArticleListItem['topic']>[];
  isPending: boolean;
  error: unknown;
  onRetry: () => void;
}

export function ArticleList({
  scope,
  search,
  onSearchChange,
  onOpen,
  onCreate,
  onEdit,
  onUpdateFaq,
  onOpenRelatedTask,
  canDelete = false,
  topics,
}: {
  scope: string;
  search: ArticleSearch;
  onSearchChange: (search: ArticleSearch) => void;
  onOpen: (id: string) => void;
  onCreate?: () => void;
  onEdit?: (id: string) => void;
  onUpdateFaq?: () => void;
  onOpenRelatedTask?: (id: string) => void;
  canDelete?: boolean;
  topics: ArticleTopicsState;
}) {
  const { t, i18n } = useTranslation();
  const query = useQuery(articleListQuery(scope, toArticleQuery(search)));
  const changeFilter = useCallback(
    (next: Partial<ArticleSearch>) => {
      onSearchChange({ ...search, ...next, articlePage: undefined });
    },
    [onSearchChange, search]
  );
  const commit = useCallback(
    (articleQ: string | undefined) => {
      changeFilter({ articleQ });
    },
    [changeFilter]
  );
  const draft = useDebouncedUrlSearch({ committedQuery: search.articleQ, onCommit: commit });
  const [deleteTarget, setDeleteTarget] = useState<ArticleListItem>();
  const closeDeletion = useCallback(() => {
    setDeleteTarget(undefined);
  }, []);
  const renderActions = (row: ArticleListItem) => {
    const relatedTask = onOpenRelatedTask ? row.relatedTask : null;
    if (row.deletion || (!onEdit && !canDelete && !relatedTask)) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t('articles.actionsFor', { title: row.title ?? t('crm.missingValue') })}
            disabled={Boolean(deleteTarget)}
            onClick={(event) => {
              event.stopPropagation();
            }}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
          >
            <MoreVertical aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {relatedTask ? (
            <DropdownMenuItem
              onSelect={() => {
                onOpenRelatedTask?.(relatedTask.id);
              }}
            >
              {t('articles.goToRelatedTicket')}
            </DropdownMenuItem>
          ) : null}
          {onEdit ? (
            <DropdownMenuItem
              onSelect={() => {
                onEdit(row.id);
              }}
            >
              {t('articles.edit')}
            </DropdownMenuItem>
          ) : null}
          {(onEdit || relatedTask) && canDelete ? <DropdownMenuSeparator /> : null}
          {canDelete ? (
            <DropdownMenuItem
              variant="destructive"
              onSelect={() => {
                setDeleteTarget(row);
              }}
            >
              {t('articles.delete')}
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };
  const missing = t('crm.missingValue');
  const fields: ResponsiveTableColumn<ArticleListItem>[] = [
    {
      key: 'title',
      header: t('articles.fields.title'),
      size: 'flex',
      cell: (row) => (
        <Text size="inherit" className="break-words">
          {row.title ?? missing}
        </Text>
      ),
    },
    {
      key: 'topic',
      header: t('articles.fields.topic'),
      size: 'medium',
      cell: (row) => articleTopicLabel(row.topic, t),
    },
    {
      key: 'status',
      header: t('articles.fields.status'),
      size: 'compact',
      cell: (row) => (
        <StatusBadge tone={row.status === 'Approved' ? 'positive' : 'neutral'}>
          {articleStatusLabel(row.status, t)}
        </StatusBadge>
      ),
    },
    {
      key: 'taskId',
      header: t('articles.fields.taskId'),
      size: 'compact',
      cell: (row) => row.relatedTask?.number ?? missing,
    },
    {
      key: 'createdAt',
      header: t('articles.fields.createdAt'),
      size: 'date',
      cell: (row) =>
        row.createdAt
          ? new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium' }).format(
              new Date(row.createdAt)
            )
          : missing,
    },
  ];
  if (onEdit || canDelete || onOpenRelatedTask)
    fields.push({
      key: 'actions',
      header: t('common.actions'),
      size: 'actionCompact',
      cell: renderActions,
    });
  const meta = query.data?.meta;
  const isLoading =
    query.isPending || query.isPlaceholderData || (query.isFetching && Boolean(deleteTarget));
  const accessDenied = isApiError(query.error) && [401, 403].includes(query.error.status);
  const showList = !accessDenied && (!query.error || Boolean(query.data));
  const knowledge = meta?.knowledge;
  // Retiring the previous file does not change the current knowledge available to staff.
  const observedKnowledgeState = query.error
    ? 'unknown'
    : knowledge?.current
      ? knowledge.eligibleCount === 0
        ? 'empty'
        : 'ready'
      : knowledge?.state;
  const observedAt = query.error
    ? query.errorUpdatedAt
    : query.isPlaceholderData
      ? 0
      : query.dataUpdatedAt;
  const [knowledgeSnapshot, setKnowledgeSnapshot] = useState<{
    scope: string;
    updatedAt: number;
    value: keyof typeof knowledgeStatusTones | undefined;
  }>({ scope, updatedAt: 0, value: undefined });
  const resetKnowledge = knowledgeSnapshot.scope !== scope || !scope || accessDenied;
  // Knowledge describes the whole corpus; an older cached page must not restore an older status.
  if (resetKnowledge) {
    if (knowledgeSnapshot.scope !== scope || knowledgeSnapshot.value) {
      setKnowledgeSnapshot({ scope, updatedAt: 0, value: undefined });
    }
  } else if (observedKnowledgeState && observedAt > knowledgeSnapshot.updatedAt) {
    setKnowledgeSnapshot({
      scope,
      updatedAt: observedAt,
      value: observedKnowledgeState,
    });
  }
  const knowledgeState = !resetKnowledge ? knowledgeSnapshot.value : undefined;
  const outOfRange = !isLoading && meta && meta.totalItems > 0 && meta.page > meta.totalPages;
  const pagination =
    meta && meta.totalItems > 0 && meta.page <= meta.totalPages ? (
      <PaginationControls
        currentPage={search.articlePage ?? 1}
        pageSize={articlePageSize}
        totalItems={meta.totalItems}
        disabled={isLoading}
        displayRange={
          isLoading
            ? {
                from: (meta.page - 1) * meta.pageSize + 1,
                to: Math.min(meta.page * meta.pageSize, meta.totalItems),
                totalItems: meta.totalItems,
              }
            : undefined
        }
        onPageChange={(articlePage) => {
          onSearchChange({ ...search, articlePage });
        }}
        labels={{
          page: t('common.pagination.page'),
          showing: (from, to, total) => t('common.pagination.showing', { from, to, total }),
          of: (count) => t('common.pagination.of', { count }),
          previous: t('common.previous'),
          next: t('common.next'),
        }}
      />
    ) : null;
  return (
    <section className="flex min-w-0 flex-col gap-4" aria-label={t('articles.title')}>
      <Heading as="h1" size="2xl" className="sr-only">
        {t('articles.title')}
      </Heading>
      {knowledgeState || onUpdateFaq ? (
        <Card role="region" aria-labelledby="article-knowledge-title">
          <CardContent className="space-y-3 p-4">
            <Heading id="article-knowledge-title" as="h2" size="md" weight="semibold">
              {t(onUpdateFaq ? 'faq.overviewTitle' : 'aiContent.knowledge')}
            </Heading>
            <DescriptionList
              columns={1}
              items={[
                ...(knowledgeState
                  ? [
                      {
                        id: 'ai-knowledge',
                        term: (
                          <span className="inline-flex items-center gap-2">
                            {t('articles.knowledge.title')}
                            <InfoHint
                              className="shrink-0"
                              text={t('articles.knowledge.description')}
                            />
                          </span>
                        ),
                        description: (
                          <span role="status" aria-label={t('articles.knowledge.title')}>
                            <StatusBadge tone={knowledgeStatusTones[knowledgeState]}>
                              {t(`articles.knowledge.states.${knowledgeState}`)}
                            </StatusBadge>
                          </span>
                        ),
                      },
                    ]
                  : []),
                ...(onUpdateFaq
                  ? [
                      {
                        id: 'faq',
                        term: (
                          <span className="inline-flex items-center gap-2">
                            {t('faq.label')}
                            <InfoHint className="shrink-0" text={t('faq.summary')} />
                          </span>
                        ),
                        description: (
                          <Button
                            variant="outline"
                            size="sm"
                            density="compact"
                            onClick={onUpdateFaq}
                          >
                            {t('faq.title')}
                          </Button>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </CardContent>
        </Card>
      ) : null}
      <div className="flex flex-wrap items-end gap-3">
        <SearchFilterToolbar className="w-full sm:w-auto sm:flex-1">
          <SearchInput
            className="min-w-0 max-w-lg flex-1"
            maxLength={200}
            aria-label={t('articles.search')}
            placeholder={t('articles.search')}
            value={draft.draft}
            onChange={(event) => {
              draft.setDraft(event.target.value);
            }}
            onCompositionStart={draft.onCompositionStart}
            onCompositionEnd={(event) => {
              draft.onCompositionEnd(event.currentTarget.value);
            }}
          />
          <FilterPopover
            label={t('crm.customerDirectory.filtersButton')}
            clearLabel={t('common.clearAll')}
            activeFilterCount={
              Number(Boolean(search.articleStatus)) + Number(Boolean(search.articleTopicId))
            }
            onClear={() => {
              changeFilter({ articleStatus: undefined, articleTopicId: undefined });
            }}
          >
            <FilterPopoverFields>
              <FilterPopoverSelect
                id="article-status-filter"
                label={t('articles.fields.status')}
                allLabel={t('common.all')}
                value={search.articleStatus}
                options={(['Draft', 'Approved'] as const).map((value) => ({
                  value,
                  label: articleStatusLabel(value, t),
                }))}
                onChange={(value) => {
                  changeFilter({
                    articleStatus: articleSearchSchema.shape.articleStatus.parse(value),
                  });
                }}
              />
              <FilterPopoverSelect
                id="article-topic-filter"
                label={t('articles.fields.topic')}
                allLabel={t('common.all')}
                value={search.articleTopicId}
                options={topics.topics.map((topic) => ({
                  value: topic.id,
                  label: articleTopicLabel(topic, t),
                }))}
                loading={topics.isPending}
                loadingLabel={t('common.loading')}
                errorLabel={topics.error ? t('articles.errors.topics') : undefined}
                retryLabel={t('auth.retry')}
                onRetry={topics.onRetry}
                onChange={(value) => {
                  changeFilter({ articleTopicId: value });
                }}
              />
            </FilterPopoverFields>
          </FilterPopover>
        </SearchFilterToolbar>
        {onCreate ? (
          <Button className="ml-auto" onClick={onCreate}>
            {t('articles.create')}
          </Button>
        ) : null}
      </div>
      {deleteTarget ? (
        <ArticleListDeletion
          key={deleteTarget.id}
          scope={scope}
          id={deleteTarget.id}
          title={deleteTarget.title ?? missing}
          canDelete={canDelete}
          onClose={closeDeletion}
        />
      ) : null}
      {query.error ? (
        <ArticleError
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : null}
      {showList ? (
        <>
          {pagination}
          <ResponsiveTable
            rows={[...(query.data?.data.items ?? [])]}
            columns={fields}
            getRowKey={(row) => row.id}
            getRowNavigation={(row) => ({
              ariaLabel: row.title ?? missing,
              onNavigate: () => {
                onOpen(row.id);
              },
            })}
            loadingGeometryKey={scope}
            breakpointClassName="lg"
            columnLayout="stable"
            isLoading={isLoading}
            loadingRowCount={articlePageSize}
            loadingLabel={t('common.loading')}
            ariaLabel={t('articles.title')}
            emptyState={outOfRange ? false : { title: t('articles.empty') }}
            renderMobileCard={(row) => (
              <Card interactive>
                <CardContent className="space-y-4 p-4">
                  <CardHeaderRow
                    controls={
                      <div className="flex items-center gap-2">
                        <StatusBadge tone={row.status === 'Approved' ? 'positive' : 'neutral'}>
                          {articleStatusLabel(row.status, t)}
                        </StatusBadge>
                        {renderActions(row)}
                      </div>
                    }
                  >
                    <Text className="min-w-0 break-words font-medium">{row.title ?? missing}</Text>
                  </CardHeaderRow>
                  <DescriptionList
                    columns={1}
                    mobileLayout="inline"
                    items={fields
                      .filter((field) => !['title', 'status', 'actions'].includes(field.key))
                      .map((field) => ({
                        id: field.key,
                        term: field.header,
                        description: field.cell(row),
                      }))}
                  />
                </CardContent>
              </Card>
            )}
          />
          {pagination}
          {outOfRange ? (
            <Button
              variant="outline"
              onClick={() => {
                onSearchChange({ ...search, articlePage: meta.totalPages });
              }}
            >
              {t('helpdesk.lastPage')}
            </Button>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
